import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollText, Plus, Trash2 } from "lucide-react";

const CATS = ["childhood", "career", "graduation", "relationship", "family", "achievement", "loss", "recovery", "breakthrough", "dream", "milestone"];

export default function LifeStory() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", year: "", category: "milestone" });

  const load = () => api.get("/lifestory").then(({ data }) => setEvents(data.events || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim()) return toast.error("Give this chapter a title");
    try { await api.post("/lifestory", { ...form, year: form.year ? parseInt(form.year) : null }); setOpen(false); setForm({ title: "", description: "", year: "", category: "milestone" }); load(); toast.success("Added to your story"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/lifestory/${id}`); load(); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold sm:text-4xl">Life Story</h1>
          <p className="mt-2 max-w-lg text-muted-foreground">A meaningful timeline of your chapters. Your companion connects today's moments to how far you've come.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="add-event-button"><Plus className="h-4 w-4" strokeWidth={1.5} /> Add chapter</Button></DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Add a life chapter</DialogTitle></DialogHeader>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What happened?" data-testid="event-title-input" className="rounded-xl" />
            <div className="flex gap-2">
              <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year" data-testid="event-year-input" className="rounded-xl w-28" />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-xl flex-1 capitalize" data-testid="event-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What made it meaningful?" data-testid="event-desc-input" className="rounded-2xl resize-none" />
            <Button onClick={add} className="rounded-full" data-testid="save-event-button">Add to story</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mt-10 pl-8">
        <div className="absolute left-3 top-2 bottom-2 w-px bg-border" aria-hidden />
        {events.length === 0 && <p className="text-sm text-muted-foreground">Your story starts whenever you're ready.</p>}
        {events.map((e, i) => (
          <motion.div key={e.event_id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.06 }}
                      className="group relative mb-8">
            <div className="absolute -left-[1.35rem] top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary ring-4 ring-background">
              <ScrollText className="h-3 w-3 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <div className="ambient-shadow rounded-3xl border border-border/60 bg-card p-5">
              <div className="flex items-start justify-between">
                <div>
                  {e.year && <span className="text-xs font-medium text-primary">{e.year}</span>}
                  <h3 className="font-serif text-lg">{e.title}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs capitalize text-secondary-foreground">{e.category}</span>
                  <button onClick={() => del(e.event_id)} className="text-muted-foreground opacity-0 group-hover:opacity-100" data-testid={`delete-event-${e.event_id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
                </div>
              </div>
              {e.description && <p className="mt-2 text-sm text-muted-foreground">{e.description}</p>}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
